import { graphql, useFragment } from 'react-relay';

import {
  PAYMENT_METHOD_TYPE,
  SUBSCRIPTION_INSTALLMENT_STATUS_ENUM,
} from '@woovi/shared';

import type { PaymentActiveInstallment_charge$key } from './__generated__/PaymentActiveInstallment_charge.graphql.ts';
import type { PaymentActiveInstallment_query$key } from './__generated__/PaymentActiveInstallment_query.graphql.ts';
import PaymentActiveInstallmentCompleted from './PaymentActiveInstallmentCompleted.tsx';
import PaymentActiveInstallmentExpired from './PaymentActiveInstallmentExpired.tsx';
import PaymentActiveInstallmentScheduled from './PaymentActiveInstallmentScheduled.tsx';
import PaymentActiveCob from '../PaymentActiveCob.tsx';

import type { ReactNode } from 'react';

type PaymentActiveInstallmentProps = {
  query: PaymentActiveInstallment_query$key;
  charge: PaymentActiveInstallment_charge$key;
};

const PaymentActiveInstallment = (
  props: PaymentActiveInstallmentProps,
): ReactNode => {
  const query = useFragment<PaymentActiveInstallment_query$key>(
    graphql`
      fragment PaymentActiveInstallment_query on Query {
        ...PaymentActiveInstallmentCompleted_query
      }
    `,
    props.query,
  );

  const charge = useFragment<PaymentActiveInstallment_charge$key>(
    graphql`
      fragment PaymentActiveInstallment_charge on Charge {
        id
        paymentMethods {
          ... on PaymentMethodInstallments {
            method
            subscription {
              currentInstallment {
                status
                charge {
                  status
                  brCode
                  ...PaymentActiveCob_charge
                  ...PaymentActiveInstallmentCompleted_charge
                  ...PaymentActiveInstallmentExpired_charge
                  ...PaymentActiveInstallmentScheduled_charge
                }
              }
            }
          }
        }
      }
    `,
    props.charge,
  );

  if (
    !charge?.paymentMethods?.length ||
    charge?.paymentMethods[0]?.method !== PAYMENT_METHOD_TYPE.PIX_CREDIARY
  ) {
    return null;
  }

  const { currentInstallment } = charge!.paymentMethods[0]!.subscription;

  if (
    currentInstallment.status === SUBSCRIPTION_INSTALLMENT_STATUS_ENUM.ACTIVE
  ) {
    return <PaymentActiveCob charge={currentInstallment.charge} />;
  }

  if (
    currentInstallment.status === SUBSCRIPTION_INSTALLMENT_STATUS_ENUM.COMPLETED
  ) {
    return (
      <PaymentActiveInstallmentCompleted
        query={query}
        charge={currentInstallment.charge}
      />
    );
  }

  if (
    currentInstallment.status === SUBSCRIPTION_INSTALLMENT_STATUS_ENUM.EXPIRED
  ) {
    return (
      <PaymentActiveInstallmentExpired charge={currentInstallment.charge} />
    );
  }

  return (
    <PaymentActiveInstallmentScheduled charge={currentInstallment.charge} />
  );
};

export default PaymentActiveInstallment;
